import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  Game,
  GameRugbyPosition,
  GameRugbyPositionLocalization,
  GameStatuses,
  GameUser,
  generateArray,
  getEnumOptions, RUGBY_GAME_LOG_TYPE_POINTS,
  RugbyGameLog,
  RugbyGameLogTypes,
  RugbyGameStatistic,
  Team,
  Tournament
} from '@mtgame/core';
import { getHeaderHeight } from '@utils/layout';
import { getLeaguePlayerRoute, getTeamPageRoute } from '@config/routes';

@Component({
  selector: 'mtg-rugby-game-protocol',
  templateUrl: './rugby-game-protocol.component.html',
  styleUrls: ['./rugby-game-protocol.component.scss', '../../game/styles/protocol.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RugbyGameProtocolComponent implements OnInit, OnChanges {
  @Input()
  users: GameUser[] = [];
  @Input()
  gameStatistic: RugbyGameStatistic[] = [];
  @Input()
  logs: RugbyGameLog[] = [];
  @Input()
  team: Team;
  @Input()
  game: Game;
  @Input()
  tournament: Tournament;
  @Input()
  dialog: boolean;

  players: GameUser[] = [];
  filteredUsers: GameUser[] = [];
  filteredStatistic: {[key: number]: RugbyGameStatistic} = {};
  periods: number[] = [];
  userEfficiency = {};
  userMinutes = {};
  overtimePeriods: number[] = [];
  positionLocalization = GameRugbyPositionLocalization;
  positions = getEnumOptions(GameRugbyPosition);
  periodControl = new UntypedFormControl(null);
  positionControl = new UntypedFormControl(null);
  query = new UntypedFormControl();
  headerHeight = getHeaderHeight();

  constructor(
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.periodControl.valueChanges
      .subscribe(() => {
        this.updateProtocol();
      });
    this.positionControl.valueChanges
      .subscribe(() => {
        this.updateProtocol();
      });
    this.query.valueChanges
      .subscribe(() => {
        this.updateProtocol();
      });
    this.periods = generateArray(this.game.rugbyGameConfig.periodsCount);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.users || changes.logs) {
      this.updateLogs();
    }
    if ((changes.users || changes.logs || changes.period || changes.gameStatistic) &&
      this.users && this.logs && this.users.length && this.gameStatistic
    ) {
      this.updateProtocol();
    }
  }

  getTotal(key: string): number {
    const gameUserIds = this.users.map(u => u.id);
    return Object.keys(this.filteredStatistic).filter(k => gameUserIds.indexOf(+k) > -1)
      .reduce((acc, gameUserId) => acc + (this.filteredStatistic[gameUserId][key] || 0), 0);
  }

  getUserProfileRoute(gameUser: GameUser): string[] {
    return getLeaguePlayerRoute(gameUser.tournamentTeamUser.leaguePlayer.id);
  }

  getTeamRoute(teamId: number): string[] {
    return getTeamPageRoute(teamId);
  }

  updateLogs() {
    if (!this.logs || !this.users) {
      return;
    }
    this.logs = this.logs.map(item => {
      item.gameUser = this.users.find(u => u.id === item.gameUserId);
      return item;
    });
  }

  updateProtocol() {
    this.overtimePeriods = (this.logs || []).filter(l => l.period > this.game.rugbyGameConfig.periodsCount)
      .map(l => l.period)
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();
    this.filteredUsers = this.getFilteredUsers();
    this.players = [...this.filteredUsers];
    this.filteredStatistic = this.getFilteredStatistic();
    this.calculateAdditionalStatistic();
    this.cdr.markForCheck();
  }

  getFilteredUsers(): GameUser[] {
    const query = (this.query.value || '').toLowerCase();
    const position = +this.positionControl.value;

    return this.users.filter(item =>
      (!position || item.teamUser.user.rugbyProfile.position === position) &&
      (!query || item.tournamentTeamUser.id
        ? item.tournamentTeamUser.leaguePlayer.shortName.toLowerCase().indexOf(query) > - 1
        : item.teamUser.user.shortName.toLowerCase().indexOf(query)
      )
    );
  }

  getFilteredStatistic(): {[key: number]: RugbyGameStatistic} {
    const period = +this.periodControl.value;
    if (!period) {
      return (this.gameStatistic || []).reduce((acc, item) =>
        Object.assign(acc, {[item.gameUserId]: item}), {});
    }
    const logs = this.logs.filter(item => item.period === period);
    return (this.gameStatistic || []).reduce((acc, item) =>
      Object.assign(acc, {[item.gameUserId]: this.updateGameUserStats(Object.assign(new RugbyGameStatistic(), item), logs)}), {}
    );
  }

  calculateAdditionalStatistic() {
    const period = +this.periodControl.value;
    const logs = period
      ? this.logs.filter(item => item.period === period)
      : this.logs;
    const enterExitLogs = this.logs.filter(item => [RugbyGameLogTypes.enter_game, RugbyGameLogTypes.exit_game].indexOf(item.logType) > -1);
    const teamUserIds = this.users.filter(u => u.teamUser.teamId === this.game.team.id).map(u => u.id);
    const competitorUserIds = this.users.filter(u => u.teamUser.teamId === this.game.competitorTeam.id).map(u => u.id);
    const scoreLogs = logs.filter(item => item.isScoreType());
    const lastLog = this.logs.length > 0 ? this.logs[0] : null;
    if (!lastLog) {
      const emptyData = this.users.reduce((acc, item) => Object.assign(acc, {[item.id]: 0}), {});
      this.userEfficiency = emptyData;
      this.userMinutes = emptyData;
      return;
    }

    for (const user of this.users) {
      const userLogs = enterExitLogs.filter(item => item.gameUserId === user.id).reverse();
      const periods = userLogs.filter(item => item.logType === RugbyGameLogTypes.enter_game).map(item => {
        const exitLog = userLogs.find(i => {
          if (i.logType !== RugbyGameLogTypes.exit_game) {
            return false;
          }
          if (i.time === item.time && i.period === item.period) {
            return i.id > item.id;
          }
          return i.time > item.time && i.period === item.period || i.period > item.period;
        });
        return [
          item,
          exitLog
        ];
      });
      const logsInPeriod = scoreLogs.filter(item => periods.find(p => {
        let fromValid;
        let toValid;
        if (item.period === p[0].period && item.time === p[0].time) {
          fromValid = p[0].id < item.id;
        } else {
          fromValid = (item.time >= p[0].time && item.period === p[0].period) || item.period > p[0].period;
        }
        if (!p[1]) {
          toValid = true;
        } else if (item.period === p[1].period && item.time === p[1].time) {
          toValid = p[1].id > item.id;
        } else {
          toValid = (item.time <= p[1].time && item.period === p[1].period) || item.period < p[1].period;
        }
        return fromValid && toValid;
      }));
      const myTeamUsers = teamUserIds.indexOf(user.id) > -1 ? teamUserIds : competitorUserIds;
      const score = logsInPeriod.filter(item => myTeamUsers.indexOf(item.gameUserId) > -1)
        .reduce((acc, i) => acc + RUGBY_GAME_LOG_TYPE_POINTS[i.logType], 0);
      const lost = logsInPeriod.filter(item => myTeamUsers.indexOf(item.gameUserId) < 0)
        .reduce((acc, i) => acc + RUGBY_GAME_LOG_TYPE_POINTS[i.logType], 0);
      this.userEfficiency[user.id] = score - lost;

      let totalTime = periods.reduce((acc, p) => {
        let periodFrom = p[0].period;
        let timeFrom = p[0].time;
        let periodTo = p[1] && p[1].period;
        let timeTo = p[1] && p[1].time;
        if (!periodTo) {
          if (this.game.status === GameStatuses.closed) {
            if (this.overtimePeriods.length) {
              periodTo = Math.max(...this.overtimePeriods);
              timeTo = (periodTo === this.game.rugbyGameConfig.penaltyPeriod) ? 0 :
                (this.game.rugbyGameConfig.periodsCount * this.game.rugbyGameConfig.periodTime * 60) + (this.overtimePeriods.length * this.game.rugbyGameConfig.overtimeTime * 60);
            } else {
              periodTo = this.game.rugbyGameConfig.periodsCount;
              timeTo = this.game.rugbyGameConfig.periodsCount * this.game.rugbyGameConfig.periodTime * 60;
            }
          } else {
            periodTo = lastLog.period;
            timeTo = lastLog.time;
          }
        }
        if (period && (periodFrom > period || periodTo < period)) {
          return acc;
        }
        if (period) {
          if (periodFrom < period) {
            periodFrom = period;
            timeFrom = 0;
          }
          if (periodTo > period) {
            periodTo = period;
            timeTo = periodTo > this.game.rugbyGameConfig.periodsCount
              ? this.game.rugbyGameConfig.overtimeTime * 60
              : this.game.rugbyGameConfig.periodTime * 60;
          }
        }
        const time = timeTo - timeFrom;
        /*for (let i = periodFrom; i < periodTo; i++) {
          time += i === this.game.rugbyGameConfig.penaltyPeriod
            ? 0
            : i > this.game.rugbyGameConfig.periodsCount
            ? this.game.rugbyGameConfig.overtimeTime * 60
            : this.game.rugbyGameConfig.periodTime * 60;
        }*/
        return acc + time;
      }, 0);
      totalTime = totalTime || 0;
      const minutes = Math.floor(totalTime / 60);
      const seconds = totalTime - minutes * 60;
      this.userMinutes[user.id] = `${minutes < 10 ? 0 : ''}${minutes}:${seconds < 10 ? 0 : ''}${seconds}`;
    }
  }

  updateGameUserStats(gameStatistic: RugbyGameStatistic, logs: RugbyGameLog[]) {
    const userLogs = logs.filter(item => item.gameUserId === gameStatistic.gameUserId);
    const logMapping = {
      tries: RugbyGameLogTypes.try,
      penaltyTries: RugbyGameLogTypes.penalty_try,
      conversionGoals: RugbyGameLogTypes.conversion_goal,
      conversionMisses: RugbyGameLogTypes.conversion_miss,
      penaltyGoals: RugbyGameLogTypes.penalty_goal,
      penaltyMisses: RugbyGameLogTypes.penalty_miss,
      dropGoals: RugbyGameLogTypes.drop_goal,
      dropGoalMisses: RugbyGameLogTypes.drop_goal_miss,
      freeKicks: RugbyGameLogTypes.free_kick,
      offloads: RugbyGameLogTypes.offload,
      tackles: RugbyGameLogTypes.tackle,
      handlingErrors: RugbyGameLogTypes.handling_error,
      carriesOverGainline: RugbyGameLogTypes.carries_over_gainline,
      redCards: RugbyGameLogTypes.red_card,
      yellowCards: RugbyGameLogTypes.yellow_card
    };

    for (const key of Object.keys(logMapping)) {
        gameStatistic[key] = userLogs.filter(log => log.logType === logMapping[key]).length;
    }

    gameStatistic.points = (gameStatistic.penaltyTries || 0) * RUGBY_GAME_LOG_TYPE_POINTS[RugbyGameLogTypes.penalty_try] +
      (gameStatistic.tries || 0) * RUGBY_GAME_LOG_TYPE_POINTS[RugbyGameLogTypes.try] +
      (gameStatistic.conversionGoals || 0) * RUGBY_GAME_LOG_TYPE_POINTS[RugbyGameLogTypes.conversion_goal] +
      (gameStatistic.penaltyGoals || 0) * RUGBY_GAME_LOG_TYPE_POINTS[RugbyGameLogTypes.penalty_goal] +
      (gameStatistic.dropGoals || 0) * RUGBY_GAME_LOG_TYPE_POINTS[RugbyGameLogTypes.drop_goal];

    return gameStatistic;
  }

  getUserStatistic(userId: number, key: string): number {
    const userStatistic = this.filteredStatistic[userId];
    if (!userStatistic) {
      return 0;
    }
    return userStatistic[key] || 0;
  }

  identify(index: number, gameUser: GameUser): number {
    return gameUser.id;
  }

  getPercent(made, attempts): number {
    made = made || 0;
    attempts = attempts || 0;
    if (!made || !attempts) {
      return 0;
    }
    return Math.floor(1000 * made / attempts) / 10;
  }
}
